/**
 * WebSocket クライアント
 *
 * Socket.IO を使用したリアルタイム通信
 */

import { io, type Socket } from 'socket.io-client';
import type { Action, GameState } from '@/types/game';

/**
 * WebSocketイベントリスナー型定義
 */
export interface WebSocketListeners {
  onConnected?: () => void;
  onGameJoined?: (data: { game_id: string; player_position: number; message: string }) => void;
  onActionExecuted?: (data: { game_state: GameState; action: Action }) => void;
  onAIThinking?: (data: { player_position: number }) => void;
  onAIAction?: (data: { game_state: GameState; action: Action }) => void;
  onRoundStarted?: (data: { game_state: GameState; round: number }) => void;
  onGameStateUpdate?: (data: { game_state: GameState }) => void;
  onError?: (data: { message: string }) => void;
  onDisconnected?: () => void;
}

/**
 * WebSocketクライアントクラス
 */
class WebSocketClient {
  private socket: Socket | null = null;
  private listeners: WebSocketListeners = {};

  /**
   * WebSocket接続確立
   */
  connect(): void {
    // 本番環境では空文字列で同一オリジンを使用
    const url = import.meta.env.VITE_WEBSOCKET_URL || window.location.origin;

    this.socket = io(url, {
      transports: ['websocket'],
      autoConnect: true,
    });

    this.setupEventListeners();
  }

  /**
   * イベントリスナー設定
   */
  private setupEventListeners(): void {
    if (!this.socket) return;

    // 接続確立
    this.socket.on('connected', (data) => {
      console.log('✅ WebSocket接続確立:', data);
      this.listeners.onConnected?.();
    });

    // ゲーム参加成功
    this.socket.on('game_joined', (data) => {
      console.log('🎮 ゲーム参加:', data);
      this.listeners.onGameJoined?.(data);
    });

    // アクション実行通知
    this.socket.on('action_executed', (data) => {
      console.log('🎯 アクション実行:', data);
      this.listeners.onActionExecuted?.(data);
    });

    // AI思考中通知
    this.socket.on('ai_thinking', (data) => {
      console.log('🤖 AI思考中:', data);
      this.listeners.onAIThinking?.(data);
    });

    // AIアクション通知
    this.socket.on('ai_action', (data) => {
      console.log('🤖 AIアクション:', data);
      this.listeners.onAIAction?.(data);
    });

    // ラウンド開始通知
    this.socket.on('round_started', (data) => {
      console.log('🔄 新ラウンド開始:', data);
      this.listeners.onRoundStarted?.(data);
    });

    // ゲーム状態更新通知（配り直し後など）
    this.socket.on('game_state_update', (data) => {
      console.log('🔄 ゲーム状態更新:', data);
      this.listeners.onGameStateUpdate?.(data);
    });

    // エラー通知
    this.socket.on('error', (data) => {
      console.error('❌ WebSocketエラー:', data);
      this.listeners.onError?.(data);
    });

    // 切断
    this.socket.on('disconnect', () => {
      console.log('🔌 WebSocket切断');
      this.listeners.onDisconnected?.();
    });
  }

  /**
   * イベントリスナー登録
   *
   * @param listeners リスナー関数群
   */
  setListeners(listeners: WebSocketListeners): void {
    this.listeners = listeners;
  }

  /**
   * ゲーム参加
   *
   * @param gameId ゲームID
   * @param playerPosition プレイヤーポジション
   */
  joinGame(gameId: string, playerPosition: number): void {
    this.socket?.emit('join_game', {
      game_id: gameId,
      player_position: playerPosition,
    });
  }

  /**
   * アクション実行
   *
   * @param gameId ゲームID
   * @param action アクション情報
   */
  executeAction(gameId: string, action: Action): void {
    this.socket?.emit('execute_action', {
      game_id: gameId,
      ...action,
    });
  }

  /**
   * 次のラウンド開始
   *
   * @param gameId ゲームID
   */
  startNextRound(gameId: string): void {
    this.socket?.emit('start_next_round', {
      game_id: gameId,
    });
  }

  /**
   * 5し配り直し選択
   *
   * @param gameId ゲームID
   * @param redistribute true=配り直す, false=続行
   */
  redistributionDecision(gameId: string, redistribute: boolean): void {
    this.socket?.emit('redistribution_decision', {
      game_id: gameId,
      redistribute: redistribute,
    });
  }

  /**
   * 切断
   */
  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
  }

  /**
   * 接続状態確認
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }
}

// シングルトンインスタンス
export const socketClient = new WebSocketClient();
